import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Search, User, LogOut, Heart, Settings, ChevronDown } from 'lucide-react';
import '../css/Layout.css';
import SearchModal from '../components/Search';
import { useAuth } from '../context/AuthContext';
import { getAllLegalPages } from '../helpers/legal-pages';

const Layout = ({ children }) => {
  const { isAuthenticated, user, logout: contextLogout } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [legalDocs, setLegalDocs] = useState([]);

  // Refs
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      
      // Handle mobile menu
      if (mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 20;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchData();
  }, [children]);

  const fetchData = async () => {
    try {
      const legalData = await getAllLegalPages();
      setLegalDocs(legalData);
      // console.log(legalData)
    } catch (e) {
      console.error(e);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    contextLogout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Mobile user menu items component
  const MobileUserMenu = () => {
    const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
    
    return (
      <>
        <div className="mobile-menu-divider" />
        <button 
          className="mobile-user-menu-button"
          onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)}
        >
          <div className="mobile-user-info">
            <User size={20} />
            <span>{user?.username}</span>
          </div>
          <ChevronDown 
            size={20} 
            className={`mobile-menu-chevron ${isMobileUserMenuOpen ? 'open' : ''}`}
          />
        </button>
        
        {isMobileUserMenuOpen && (
          <div className="mobile-user-dropdown">
            <Link to="/profile" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <User size={20} />
              <span>Mon profil</span>
            </Link>
            <Link to="/profile/favorites" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <Heart size={20} />
              <span>Mes favoris</span>
            </Link>
            <Link to="/profile/settings" className="mobile-nav-link" onClick={toggleMobileMenu}>
              <Settings size={20} />
              <span>Paramètres</span>
            </Link>
            <button className="mobile-nav-link logout" onClick={handleLogout}>
              <LogOut size={20} />
              <span>Déconnexion</span>
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="layout">
      <div className={`navbar-container ${isScrolled ? 'scrolled' : ''}`}>
        <nav className="floating-navbar">
          <div className="nav-content">
            <Link className="brand" to="/">
              <img src="/images/logo.png" alt="Ti'Spot 971" />
            </Link>

            {/* Desktop Menu */}
            <div className="nav-links desktop-menu">
              <Link to="/" className="nav-link">Accueil</Link>
              <Link to="/carte" className="nav-link">La Carte</Link>
              <Link to="/les-spots" className="nav-link">Les Spots</Link>
              <Link to="/evenements" className="nav-link">Évènements</Link>
              <button className="search-button" onClick={() => setIsSearchOpen(true)}>
                <Search size={20} />
                <span>Rechercher</span>
              </button>
            </div>

            {/* Only show user menu in desktop view */}
            {isAuthenticated ? (
              <div className="user-menu" ref={userMenuRef}>
                <button
                  className="user-menu-button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <User size={20} />
                  <span>{user?.username}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="user-dropdown">
                    <Link to="/profile" className="dropdown-item" onClick={() => setIsUserMenuOpen(false)}>
                      <User size={16} />
                      <span>Mon profil</span>
                    </Link>
                    <Link to="/profile/favorites" className="dropdown-item" onClick={() => setIsUserMenuOpen(false)}>
                      <Heart size={16} />
                      <span>Favoris</span>
                    </Link>
                    <Link to="/profile/settings" className="dropdown-item" onClick={() => setIsUserMenuOpen(false)}>
                      <Settings size={16} />
                      <span>Paramètres</span>
                    </Link>
                    <button className="dropdown-item logout" onClick={handleLogout}>
                      <LogOut size={16} />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/connexion-inscription" className="auth-button desktop-only">
                <User size={20} />
                <span>Connexion</span>
              </Link>
            )}
            {/* </div> */}
            {/* </div> */}

            {/* Mobile Menu Toggle */}
            <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
            <Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>Accueil</Link>
            <Link to="/carte" className="mobile-nav-link" onClick={toggleMobileMenu}> La Carte</Link>
            <Link to="/les-spots" className="mobile-nav-link" onClick={toggleMobileMenu}>Les Spots</Link>
            <Link to="/evenements" className="mobile-nav-link" onClick={toggleMobileMenu}>Évènements</Link>
            <button
              className="mobile-search-button"
              onClick={() => {
                setIsSearchOpen(true)
                toggleMobileMenu()
              }}
            >
              <Search size={20} />
              <span>Rechercher</span>
            </button>

            {/* Show auth button or user menu in mobile menu */}
            {isAuthenticated ? (
              <MobileUserMenu />
            ) : (
              <>
                <div className="mobile-menu-divider" />
                <Link
                  to="/connexion-inscription"
                  className="mobile-auth-button"
                  onClick={toggleMobileMenu}
                >
                  <User size={20} />
                  <span>Connexion</span>
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>

      <main className="main-content">
        {children}
      </main>

      <footer className="modern-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h5>Ti'Spot 971</h5>
            <p>Découvrez les meilleurs spots de la Guadeloupe</p>
            <div className="social-links">
              <a href="/#" className="social-link"><i className="bi bi-instagram"></i></a>
              <a href="/#" className="social-link"><i className="bi bi-facebook"></i></a>
              <a href="/#" className="social-link"><i className="bi bi-twitter-x"></i></a>
            </div>
          </div>

          <div className="footer-section">
            <h5>Navigation</h5>
            {/* <Link to="/">Accueil</Link> */}
            <Link to="/carte">La Carte</Link>
            <Link to="/les-spots">Les Spots</Link>
            <Link to="/evenements">Évènements</Link>
            <Link to="/a-propos">À propos</Link>
            <Link to="/contact">Contact</Link>
          </div>

          <div className="footer-section">
            <h5>Mentions légales</h5>
            {legalDocs.map((item, index) =>
              <Link to={`/page/${item.slug}`} key={index}>{item.title}</Link>
            )}
          </div>

          <div className="footer-section">
            <h5>Contact</h5>
            <p><i className="bi bi-envelope"></i> support@tispot971.com</p>
            {/* <p><i className="bi bi-phone"></i> +590 590 XX XX XX</p> */}
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Ti'Spot 971 | Tous droits réservés |&nbsp;
            <a href="https://karib-tech.com" target="_blank">Créé par Karib Tech</a>
          </p>
        </div>
      </footer>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </div>
  );
};

export default Layout;